import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { useSetPassword } from '@/api/hooks/useOnboardTenant';
import type { AppError } from '@/api/client';

/**
 * One-time landing page for operators redeeming an invite token from
 * the Sprint 7 Evening 3 onboarding email. Modelled on
 * {@code ResetPasswordPage} — same URL-param shape, same token-as-auth
 * flow — but with its own copy so an invite-specific error ("invite
 * expired") doesn't read as "reset link expired", which would confuse
 * operators who never asked for a reset.
 */
export default function SetPasswordPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token');

  const mutation = useSetPassword();
  const [success, setSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const schema = z
    .object({
      newPassword: z
        .string()
        .min(8, 'At least 8 characters')
        .regex(/[A-Z]/, 'Must include an uppercase letter')
        .regex(/[a-z]/, 'Must include a lowercase letter')
        .regex(/[0-9]/, 'Must include a number'),
      confirm: z.string(),
    })
    .refine((v) => v.newPassword === v.confirm, {
      message: 'Passwords do not match',
      path: ['confirm'],
    });

  type Values = z.input<typeof schema>;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<Values>({ resolver: zodResolver(schema) });

  if (!token) {
    return (
      <Shell>
        <h1 className="text-xl font-semibold text-neutral-900">Invalid invite link</h1>
        <p className="mt-2 text-sm text-neutral-500">
          This link is missing its token. Ask your platform admin to resend the invite email.
        </p>
        <Link
          to="/login"
          className="mt-6 inline-flex items-center rounded-md border border-neutral-200 px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50"
        >
          Back to login
        </Link>
      </Shell>
    );
  }

  if (success) {
    return (
      <Shell>
        <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600" aria-hidden="true" />
        <h1 className="mt-3 text-xl font-semibold text-neutral-900">Password set</h1>
        <p className="mt-2 text-sm text-neutral-500">
          You can now log in with your new password.
        </p>
        <button
          type="button"
          onClick={() => navigate('/login')}
          className="mt-6 inline-flex items-center gap-2 rounded-md bg-[#C2713A] px-4 py-2 text-sm font-semibold text-white hover:bg-[#a95f2f]"
        >
          Log in
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </button>
      </Shell>
    );
  }

  const onSubmit = (values: Values) => {
    setSubmitError(null);
    mutation.mutate(
      { token, newPassword: values.newPassword },
      {
        onSuccess: () => setSuccess(true),
        onError: (err) => {
          const e = err as unknown as AppError;
          if (e?.code === 'INVALID_RESET_TOKEN' || e?.status === 400) {
            setSubmitError(
              'This invite link is invalid or has expired. Ask your platform admin to resend it.',
            );
          } else {
            setSubmitError('Something went wrong. Please try again in a moment.');
          }
        },
      },
    );
  };

  return (
    <Shell>
      <p className="text-xs font-mono uppercase tracking-[0.18em] text-[#C2713A]">RekPlay</p>
      <h1 className="mt-2 text-xl font-semibold text-neutral-900">Set your password</h1>
      <p className="mt-1 text-sm text-neutral-500">
        Finish setting up your operator account. You can log in immediately after.
      </p>

      <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-4 text-left" noValidate>
        <label className="block text-sm">
          <span className="mb-1 block text-xs font-medium text-neutral-500">New password</span>
          <input
            type="password"
            autoComplete="new-password"
            {...register('newPassword')}
            className="w-full rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-900 focus-visible:border-[#C2713A] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C2713A]/30"
          />
          {errors.newPassword && (
            <span className="mt-1 block text-xs text-red-600">{errors.newPassword.message}</span>
          )}
        </label>

        <label className="block text-sm">
          <span className="mb-1 block text-xs font-medium text-neutral-500">Confirm password</span>
          <input
            type="password"
            autoComplete="new-password"
            {...register('confirm')}
            className="w-full rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-900 focus-visible:border-[#C2713A] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C2713A]/30"
          />
          {errors.confirm && (
            <span className="mt-1 block text-xs text-red-600">{errors.confirm.message}</span>
          )}
        </label>

        {submitError && (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            {submitError}
          </div>
        )}

        <button
          type="submit"
          disabled={isSubmitting || mutation.isPending}
          className="w-full rounded-md bg-[#C2713A] px-4 py-2 text-sm font-semibold text-white hover:bg-[#a95f2f] disabled:cursor-not-allowed disabled:opacity-60"
        >
          {mutation.isPending ? 'Setting password…' : 'Set password and continue'}
        </button>
        <p className="text-center text-xs text-neutral-400">
          Trouble? Email <a href="mailto:support@rekplay.io" className="underline">support@rekplay.io</a>
        </p>
      </form>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-neutral-50 px-4 py-12">
      <div className="w-full max-w-md rounded-xl border border-neutral-200 bg-white p-8 text-center shadow-sm">
        {children}
      </div>
    </div>
  );
}
